import Avatar from 'ext-ui/components/Avatar/Avatar'
import Pill from 'ext-ui/components/Pill/Pill'
import React from 'react'
import { useTranslate } from 'src/localization/Trns'
import {
  formatActivityTimestamp,
  formatActivityTimestampExact,
  toActivityDateTimeAttribute,
} from 'src/page-components/Accounts/AccountActivityTab/accountActivity.format'
import type { ActivityEvent } from 'src/page-components/Accounts/AccountActivityTab/accountActivityDemoData'
import * as style from 'src/page-components/Accounts/AccountActivityTab/components/ActivityEventRow/ActivityEventRow.style'

/** ADS Avatar `sm` is 24×24 (`specs/components/avatar.spec.json`). */
const AVATAR_SIZE = 24

interface ActivityEventRowProps {
  event: ActivityEvent
  /**
   * Set by the feed on the first row of a newly appended page, so focus can be
   * moved here when the "Load more" control unmounts itself. See `ActivityFeed`.
   */
  rowRef?: React.Ref<HTMLLIElement>
}

/**
 * One attributed activity event (EX-40600) — the design's
 * `AttributedActivityEventRow`, feature-local by the deliverable's own decision
 * (no ADS spec exists for it yet; that promotion is explicitly deferred).
 *
 * Anatomy, per the design: avatar, then a single sentence of
 * `actor + message + optional detail`, with the relative time beneath it. Only
 * the actor is emphasized.
 *
 * ## Outcome: no tone dot, but `FAILED` is still marked
 *
 * The design shows a trailing coloured dot with four tones (info / warning /
 * severe / success). The DOT is omitted on purpose — the contract cannot feed
 * four tones (`outcome` is only `SUCCEEDED` or `FAILED`), and inventing a colour
 * map here would outrun it.
 *
 * Dropping the dot is not the same as dropping the outcome, though, and the
 * first pass conflated the two. Two values are exactly enough to separate "this
 * happened" from "this didn't", and on an audit feed that is the load-bearing
 * distinction — a limit increase that took effect versus one that did not. With
 * `outcome` unread, those two rows were pixel-identical whenever `detail` was
 * null, which the contract explicitly permits.
 *
 * So `FAILED` renders a trailing text marker. Text, not colour: the design's own
 * guardrail is that event meaning survives without the tone dot, and a `Pill`
 * carries the word as well as the token, so nothing here depends on colour
 * perception.
 *
 * ## Text is server-authored
 *
 * `message` and `detail` are display strings the backend owns, and the contract
 * forbids clients reconstructing them from internal actions or metadata. They
 * are therefore rendered verbatim and NOT passed through `translate` — the
 * backend localizes them. Only our own chrome (the "Just now" label) is
 * translated here.
 */
const ActivityEventRow: React.FC<ActivityEventRowProps> = ({
  event,
  rowRef,
}) => {
  const translate = useTranslate()
  const { actor, message, detail, occurredAt, outcome } = event

  return (
    // tabIndex={-1} keeps the row out of the tab sequence — it is not
    // interactive — while still allowing the feed to move focus here
    // programmatically after appending a page.
    <li
      ref={rowRef}
      tabIndex={-1}
      css={style.row}
      data-testid="activityEventRow"
    >
      {/*
        aria-hidden: the avatar depicts the actor whose name is already the first
        words of the sentence beside it, so an accessible name here would make
        every row announce the actor twice. `initials` is always present in the
        contract, so there is always a visible fallback when `avatarUrl` is null
        or fails to load.
      */}
      {/*
        `fallback` is a NODE, not a string, and that is load-bearing: Avatar
        truncates a string fallback to its first character
        (`fallback.charAt(0)`), which renders the contract's two-letter
        `initials` as a single letter — "AD" as "A". Passing a node returns it
        verbatim, and it still lands inside Avatar's `.fallback` wrapper with the
        same `fallback-text` sizing, so nothing else about the avatar changes.
      */}
      <Avatar
        css={style.avatar}
        size={AVATAR_SIZE}
        imageSrc={actor.avatarUrl ?? undefined}
        fallback={<span className="fallback-text">{actor.initials}</span>}
        aria-hidden
        data-testid="activityEventRow-avatar"
      />
      <div css={style.content}>
        <span css={style.sentence}>
          <span css={style.actor} data-testid="activityEventRow-actor">
            {actor.displayName}
          </span>{' '}
          {message}
          {detail !== null && (
            <span css={style.detail} data-testid="activityEventRow-detail">
              {' · '}
              {detail}
            </span>
          )}
        </span>
        {/*
          Renders on FAILED only. A successful event needs no marker — it is the
          default reading of the sentence — while a failed one is otherwise
          indistinguishable from it whenever `detail` is null.
        */}
        {outcome === 'FAILED' && (
          <span css={style.outcome}>
            <Pill
              color="severe"
              size="small"
              data-testid="activityEventRow-failed"
            >
              {translate('Failed')}
            </Pill>
          </span>
        )}
        {/*
          The relative label is lossy for an audit surface, so the exact time
          rides along in `title` and `dateTime` rather than as a second visible
          line the design does not have.
        */}
        <time
          css={style.timestamp}
          dateTime={toActivityDateTimeAttribute(occurredAt)}
          title={formatActivityTimestampExact(occurredAt)}
          data-testid="activityEventRow-timestamp"
        >
          {formatActivityTimestamp(occurredAt, translate('Just now'))}
        </time>
      </div>
    </li>
  )
}

export default ActivityEventRow
